import { Children, useRef, useCallback, useEffect, cloneElement } from 'react';
import { IconNames } from '@shipengine/giger-theme';
import { useKeyPress } from '../../../utils/hooks/useKeyPress.js';
import { useToggle } from 'react-use';
import { Typography } from '../../../components/Typography/Typography.js';
import { MultiSelectOptionTagsContainer } from './MultiSelectOptionTagsContainer.js';
import { getFieldContainerStyles, getFieldContentStyles, getPlaceholderStyles, getTagsContainerStyles, getSideContentStyles } from './MultiSelectStyles.js';
import { jsxs, Fragment, jsx } from '@emotion/react/jsx-runtime';
import { FieldContainer } from '../../../components/Form/Field/FieldContainer/FieldContainer.js';
import { FieldContent } from '../../../components/Form/Field/FieldContent/FieldContent.js';
import { FieldLabel } from '../../../components/Form/Field/FieldLabel/FieldLabel.js';
import { FieldSideContent } from '../../../components/Form/Field/FieldSideContent/FieldSideContent.js';
import { FieldRightIcon } from '../../../components/Form/Field/FieldRightIcon/FieldRightIcon.js';
import { DropdownOptionList } from '../../../components/DropdownOptionList/DropdownOptionList.js';

const MultiSelect = _ref => {
  let {
    children,
    disabled = false,
    label,
    maxVisibleOptions = 5,
    name,
    optionsHeaderText,
    isOpen = false,
    placement = 'bottom-start',
    toggleLabel = 'Toggle multi select',
    removeTagLabel = 'Remove',
    hideWhenReferenceNotVisible = true,
    placeholder,
    onChange,
    onClickAway,
    onOptionSelected: onOptionSelectedProp,
    showSelectedOptions = true,
    showLabel = true,
    value = [],
    ...rest
  } = _ref;
  const childrenArray = Children.toArray(children);
  const [isDropdownOpen, toggle] = useToggle(false);
  const multiSelectRef = useRef(null);
  const optionTagsDivRef = useRef(null);
  const dropdownOptionListRef = useRef(null);
  const fieldContentRef = useRef(null);
  const labelAvailable = showLabel && !!label;
  const placeholderAvailable = !showLabel && !value.length && placeholder;
  const onOptionSelected = useCallback(optionSelected => {
    let optionsToSave = value;
    onOptionSelectedProp === null || onOptionSelectedProp === void 0 ? void 0 : onOptionSelectedProp(name, optionSelected);
    if (value.find(selectedOption => selectedOption.value === optionSelected.value)) {
      var _optionsToSave;
      /** Remove a selected option */
      optionsToSave = (_optionsToSave = optionsToSave) === null || _optionsToSave === void 0 ? void 0 : _optionsToSave.filter(selectedOptionToSave => selectedOptionToSave.value !== optionSelected.value);
    } else {
      /** Add a new selected option */
      optionsToSave = [...optionsToSave, optionSelected];
    }
    onChange === null || onChange === void 0 ? void 0 : onChange(name, optionsToSave);
  }, [name, onChange, onOptionSelectedProp, value]);
  const onOptionRemove = useCallback((event, option) => {
    event === null || event === void 0 ? void 0 : event.stopPropagation();
    const optionsToSave = value.filter(oldStateOption => oldStateOption.value !== option);
    onChange === null || onChange === void 0 ? void 0 : onChange(name, optionsToSave);
  }, [name, onChange, value]);
  const handleClickAway = event => {
    var _optionTagsDivRef$cur, _multiSelectRef$curre;
    // If we click "inside" the MultiSelect do not close the DropdownOptionList
    if ((_optionTagsDivRef$cur = optionTagsDivRef.current) !== null && _optionTagsDivRef$cur !== void 0 && _optionTagsDivRef$cur.contains(event.target) || (_multiSelectRef$curre = multiSelectRef.current) !== null && _multiSelectRef$curre !== void 0 && _multiSelectRef$curre.contains(event.target)) {
      return;
    }
    onClickAway === null || onClickAway === void 0 ? void 0 : onClickAway(name, value);
    isDropdownOpen && toggle();
  };
  const handleOnFieldContainerKeyDown = event => {
    var _dropdownOptionListRe;
    return isDropdownOpen ? (_dropdownOptionListRe = dropdownOptionListRef.current) === null || _dropdownOptionListRe === void 0 ? void 0 : _dropdownOptionListRe.onKeyDown(event) : handleKeyPress(event);
  };
  const handleOnClick = useCallback(() => toggle(), [toggle]);
  const {
    handleKeyPress
  } = useKeyPress({
    onArrowDown: toggle,
    onArrowUp: toggle,
    onEspace: toggle,
    onEnter: toggle
  });
  useEffect(() => {
    if (isOpen) {
      handleOnClick();
    }
  }, [handleOnClick, isOpen]);
  return jsxs(Fragment, {
    children: [jsxs(FieldContainer, {
      id: name,
      tabIndex: 0,
      role: "button",
      ref: multiSelectRef,
      onClick: handleOnClick,
      isFocused: isDropdownOpen,
      css: getFieldContainerStyles(disabled),
      onKeyDown: !disabled ? handleOnFieldContainerKeyDown : undefined,
      disabled: disabled,
      ...rest,
      children: [jsxs(FieldContent, {
        ref: fieldContentRef,
        css: getFieldContentStyles(labelAvailable),
        children: [labelAvailable && jsx(FieldLabel, {
          htmlFor: name,
          isDisabled: disabled,
          hasFocusedValue: isDropdownOpen || showSelectedOptions && value.length > 0,
          children: label
        }), placeholderAvailable && jsx(Typography, {
          css: getPlaceholderStyles,
          tabIndex: 0,
          onKeyUp: handleKeyPress,
          variant: "body2",
          children: placeholder
        }), showSelectedOptions && value.length > 0 && jsx(MultiSelectOptionTagsContainer, {
          ref: optionTagsDivRef,
          options: value,
          removeTagLabel: removeTagLabel,
          parentContainer: fieldContentRef.current,
          onTagRemoveClick: onOptionRemove,
          css: getTagsContainerStyles(labelAvailable)
        })]
      }), jsx("input", {
        type: "hidden",
        name: name,
        value: value.join(';')
      }), jsx(FieldSideContent, {
        align: "right",
        css: getSideContentStyles(disabled),
        children: jsx(FieldRightIcon, {
          id: name,
          iconLabel: toggleLabel,
          disabled: disabled,
          iconName: isDropdownOpen ? IconNames.CHEVRON_TOP : IconNames.CHEVRON_BOTTOM
        })
      })]
    }), jsx(DropdownOptionList, {
      hideWhenReferenceNotVisible: hideWhenReferenceNotVisible,
      ref: dropdownOptionListRef,
      isOpen: isDropdownOpen,
      reference: multiSelectRef.current,
      placement: placement,
      optionsHeaderText: optionsHeaderText,
      maxVisibleOptions: maxVisibleOptions,
      dropdownWidth: "auto",
      resetFocusOnSelect: false,
      onChange: onOptionSelected,
      onClickAway: handleClickAway,
      onEscape: toggle,
      children: childrenArray.map(option => {
        const optionValue = typeof option.props.value === 'string' ? option.props.value : option.props.value.value;
        return /*#__PURE__*/cloneElement(option, {
          selected: !!value.find(optionSelected => optionSelected.value === optionValue)
        });
      })
    })]
  });
};

export { MultiSelect };
//# sourceMappingURL=MultiSelect.js.map
